import React, { useState, useEffect } from 'react';
import { X, Cookie, Shield, Info } from 'lucide-react';

const CookieConsent = () => {
  const [showConsent, setShowConsent] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made consent choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowConsent(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const allPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allPreferences);
    localStorage.setItem('cookie-consent', JSON.stringify(allPreferences));
    localStorage.setItem('cookie-consent-timestamp', new Date().toISOString());
    setShowConsent(false);
  };

  const handleAcceptSelected = () => {
    localStorage.setItem('cookie-consent', JSON.stringify(preferences));
    localStorage.setItem('cookie-consent-timestamp', new Date().toISOString());
    setShowConsent(false);
  };

  const handleRejectAll = () => {
    const minimalPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(minimalPreferences);
    localStorage.setItem('cookie-consent', JSON.stringify(minimalPreferences));
    localStorage.setItem('cookie-consent-timestamp', new Date().toISOString());
    setShowConsent(false);
  };

  const updatePreference = (category, value) => {
    setPreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  if (!showConsent) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-2xl z-50">
      <div className="max-w-6xl mx-auto p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Icon and Title */}
          <div className="flex items-start gap-4 lg:w-1/3">
            <div className="w-12 h-12 bg-violet-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Cookie className="w-6 h-6 text-violet-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-800 mb-2">Privacy & Cookies</h3>
              <p className="text-sm text-gray-600">
                We use cookies to enhance your experience and analyze site usage. Your privacy matters to us.
              </p>
            </div>
          </div>

          {/* Cookie Categories */}
          <div className="lg:w-1/3">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center gap-2 text-sm text-violet-600 hover:text-violet-700 mb-3"
            >
              <Info className="w-4 h-4" />
              {showDetails ? 'Hide' : 'Show'} cookie details
            </button>
            
            {showDetails && (
              <div className="space-y-3 p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-800">Essential Cookies</span>
                    </div>
                    <p className="text-xs text-gray-600">Required for basic site functionality</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.necessary}
                    disabled
                    className="w-4 h-4 text-violet-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Info className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-800">Analytics Cookies</span>
                    </div>
                    <p className="text-xs text-gray-600">Help us improve our website</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => updatePreference('analytics', e.target.checked)}
                    className="w-4 h-4 text-violet-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Info className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-800">Marketing Cookies</span>
                    </div>
                    <p className="text-xs text-gray-600">For personalized advertising</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => updatePreference('marketing', e.target.checked)}
                    className="w-4 h-4 text-violet-600 rounded"
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Info className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-800">Functional Cookies</span>
                    </div>
                    <p className="text-xs text-gray-600">Enable enhanced features</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => updatePreference('functional', e.target.checked)}
                    className="w-4 h-4 text-violet-600 rounded"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3 lg:w-1/3 lg:justify-end">
            <button
              onClick={handleRejectAll}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptSelected}
              className="px-4 py-2 text-sm bg-violet-100 text-violet-700 rounded-lg hover:bg-violet-200 transition-colors"
            >
              Accept Selected
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-4 py-2 text-sm bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>

        {/* Privacy Policy Links */}
        <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4 text-xs text-gray-500">
          <a href="#" className="hover:text-violet-600 transition-colors">Privacy Policy</a>
          <a href="#" className="hover:text-violet-600 transition-colors">Cookie Policy</a>
          <a href="#" className="hover:text-violet-600 transition-colors">Terms of Service</a>
          <span className="ml-auto">Last updated: {new Date().toLocaleDateString()}</span>
        </div>
      </div>

      {/* Close button for mobile */}
      <button
        onClick={() => setShowConsent(false)}
        className="lg:hidden absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
      >
        <X className="w-5 h-5" />
      </button>
    </div>
  );
};

export default CookieConsent;
